import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { http, unwrap } from './client'
import { retryUnlessRefused, workspacePath } from './workspaces'
import type { ApiEnvelope } from '@/types/api'
import type { ManagementFunction } from '@/types/intelligence'

/** My function (S0 §18.0a): the reader's own, per workspace; null until they declare it. */
export interface MyFunction {
  function: ManagementFunction | null
}

export const myFunctionKeys = {
  one: (workspaceId: string) => ['workspaces', workspaceId, 'my-function'] as const,
}

export const useMyFunction = (workspaceId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: computed(() => myFunctionKeys.one(toValue(workspaceId) ?? '')),
    queryFn: async () => unwrap((await http.get<ApiEnvelope<MyFunction>>(`${workspacePath(toValue(workspaceId) ?? '')}/my-function`)).data),
    enabled: computed(() => !!toValue(workspaceId) && toValue(enabled)),
    retry: retryUnlessRefused,
  })

export const useDeclareFunction = (workspaceId: MaybeRefOrGetter<string>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (declared: ManagementFunction) =>
      unwrap((await http.put<ApiEnvelope<MyFunction>>(`${workspacePath(toValue(workspaceId))}/my-function`, { function: declared })).data),
    onSuccess: (saved) => queryClient.setQueryData(myFunctionKeys.one(toValue(workspaceId)), saved),
  })
}
